import { formatUtcFetchTimestamp } from '@/utils/dateutil';
import React from 'react';
import { ResidentListStatus } from '@/components/residentListStatus';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { css } from '@emotion/react';
import dayjs, { Dayjs } from 'dayjs';
import { Order } from '../../../types/api/order/getOrders';
import { useFetchNursingHomeCareRecord } from '../../../hooks/api/traversal/useFetchNursingHomeCareRecord';
import { ResidentListHeader } from '../../../components/residentListHeader';
import { DateSelector } from '../../../components/mui/dateSelector';
import { residentListSettingsSelector } from '../../../redux/config/residentListSettings';
import { showMedicalContentSelector } from '../../../redux/config/selfData';
import { color } from '../../../styles';
import { ResidentRow } from './residentRow';

export interface IGetOrder extends Order {
  displayUnitName: string; // 施設名がないものを施設全体として変換したもの
  displayTimestamp: string; // 日付をutcからタイムゾーン変換したもの
}

const TopContainer = css`
  position: sticky;
  z-index: 1;
  top: 68px;
  width: 100%;
  padding: 8px 16px 12px;
  background-color: ${color.white};
  border-radius: 8px 8px 0 0;
`;

const Container = css`
  padding: 0 16px;
`;

const RightElementContainer = css`
  margin-right: 16px;
`;

const Card = css`
  max-height: 80vh;
  padding: 0 16px 16px;
  width: 100%;
  border-radius: 0 0 8px 8px;
  background-color: ${color.white};
  overflow-x: auto;
`;

const TableLegend = (w: number) => css`
  position: relative;
  left: calc(${`100% - ${w}px`});
  width: fit-content;
  padding-left: 45px;

  ::before {
    position: absolute;
    left: 0;
    display: block;
    width: 34px;
    height: 100%;
    content: '';
    background-color: ${color.light_yellow};
  }
`;

const Table = css`
  border: 1px solid ${color.table_green};
  box-sizing: border-box;
  width: 100%;
`;

const TableHead = css`
  position: sticky;
  z-index: 1;
  top: var(--sticky-top, 0);
  background: ${color.table_green};
  color: ${color.dark_gray};
`;

const ColHeaderCell = (minWidth: string, fontSize?: string) => css`
  min-width: ${minWidth};
  border-right: 1px solid ${color.white};
  border-bottom: 1px solid ${color.border_gray};
  font-size: ${fontSize ?? '10px'};
  font-weight: normal;
  word-break: break-word;
  overflow-wrap: break-word;

  &:last-of-type {
    border-right: 1px solid ${color.table_green};
  }
`;

export const ExcretionTable = React.memo(() => {
  const hourPerDay = 24;

  const initialDate = dayjs().set('hour', 0).set('minute', 0).set('second', 0).utc();
  const {
    categories: storeCategories,
    sortType: storeSortType,
  } = useSelector(residentListSettingsSelector);
  const showMedicalContent = useSelector(showMedicalContentSelector);
  const [selectedDate, setSelectDate] = React.useState<Dayjs>(initialDate);
  const { t } = useTranslation();
  const { nursingHomeCareRecord, fetchNursingHomeCareRecord, fetchStatus } = useFetchNursingHomeCareRecord(
    true,
    storeCategories,
    formatUtcFetchTimestamp(selectedDate),
    formatUtcFetchTimestamp(selectedDate.add(1, 'day')),
    storeSortType
  );
  const legendRef = React.useRef<HTMLDivElement>(null);

  const handleUpdate = () => {
    fetchNursingHomeCareRecord();
  };

  const handleChangeDate = (newDate: Dayjs) => {
    setSelectDate(newDate);
  };

  return (
    <div css={Container}>
      <ResidentListHeader
        title={t('excretionTable', '排泄表')}
        categories={storeCategories}
        sortType={storeSortType}
        rightElement={
          <div css={RightElementContainer}>
            <DateSelector
              newValue={selectedDate}
              minDate={initialDate.subtract(3, 'years')}
              maxDate={initialDate}
              onChange={handleChangeDate}
            />
          </div>
        }
        onUpdate={handleUpdate}
      />
      {nursingHomeCareRecord && nursingHomeCareRecord.residents.length ? (
        <>
          {!showMedicalContent && (
            <div css={TopContainer}>
              <div ref={legendRef} css={TableLegend(legendRef.current?.offsetWidth ?? 150)}>
                {t('toiletCareTime', '排泄ケア時刻')}
              </div>
            </div>
          )}
          <div css={Card}>
            <table css={Table}>
              <thead css={TableHead}>
                <tr>
                  <th css={ColHeaderCell('160px')}></th>
                  <th css={ColHeaderCell('38px')}>{t('excretion', '排泄')}</th>
                  <th css={ColHeaderCell('30px')}>{t('visits', '回数')}</th>
                  {Array.from({ length: hourPerDay }, (_, i) => (
                    <th key={i} css={ColHeaderCell('34px', '12px')}>
                      {i}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {nursingHomeCareRecord.residents.map((v, i) => {
                  return <ResidentRow key={i} idx={i} resident={v} />;
                })}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <ResidentListStatus status={fetchStatus} />
      )}
    </div>
  );
});
